import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TextField,
  Stack,
  Alert,
  Box,
  Slide,
  Typography,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import { EasyCheckButton } from '../../common/Controls';
import {
  VerifiedUser,
  ArrowBack
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { themeUtils } from '../../../theme';

const OtpContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: themeUtils.gradients.backgroundLight,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: themeUtils.gradients.primary,
    opacity: 0.05,
    zIndex: 0,
  }
}));

const OtpCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(5),
  boxShadow: `0 20px 60px rgba(37, 99, 235, 0.15)`,
  border: `1px solid ${themeUtils.colors.blue['blue-100']}`,
  maxWidth: '420px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  margin: theme.spacing(2),
}));

const LogoText = styled(Typography)(() => ({
  fontSize: '2rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
}));

const LogoEasy = styled('span')(() => ({
  color: '#0f172a',
}));

const LogoCheck = styled('span')(() => ({
  color: '#2563eb',
}));

const OtpInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '0.5em',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
    },
    '& fieldset': {
      borderColor: themeUtils.colors.blue['blue-200'],
      borderWidth: '2px',
    },
    '&.Mui-focused fieldset': {
      borderColor: themeUtils.colors.blue['blue-600'],
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px',
    textAlign: 'center',
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  '& .MuiCheckbox-root': {
    color: themeUtils.colors.blue['blue-400'],
    '&.Mui-checked': {
      color: themeUtils.colors.blue['blue-600'],
    },
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
  },
}));

const AnimatedBox = styled(Box)(() => ({
  animation: 'fadeInUp 0.6s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const COOLDOWN_SECONDS = 60;

const OtpVerificationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, resendOtp } = useEasyCheck();
  const inputRef = useRef(null);

  // Get email and password passed from login
  const email = location.state?.email;
  const password = location.state?.password;

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect to login if no email in state
  useEffect(() => {
    if (!email || !password) {
      navigate('/login');
    }
  }, [email, password, navigate]);

  // Auto-focus the OTP input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP code.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await verifyOtp({
        email,
        password,
        otp,
        trustDevice
      });

      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    try {
      setResendLoading(true);
      setError('');
      const result = await resendOtp(email);
      if (result.success) {
        setSuccessMessage('A new OTP has been sent to your email.');
        setResendCooldown(COOLDOWN_SECONDS);
        setOtp('');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(result.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) return null;

  return (
    <OtpContainer>
      <OtpCard>
        <AnimatedBox>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LogoText>
              <LogoEasy>Easy</LogoEasy>
              <LogoCheck>Check</LogoCheck>
            </LogoText>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: `${themeUtils.colors.blue['blue-50']}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}>
              <VerifiedUser sx={{ fontSize: 32, color: themeUtils.colors.blue['blue-600'] }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Verify Your Identity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We sent a 6-digit code to <strong>{email}</strong>
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {successMessage && (
                <Slide direction="down" in={!!successMessage} mountOnEnter unmountOnExit>
                  <Alert
                    severity="success"
                    sx={{
                      borderRadius: '12px',
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      border: '1px solid rgba(76, 175, 80, 0.2)',
                    }}
                    onClose={() => setSuccessMessage('')}
                  >
                    {successMessage}
                  </Alert>
                </Slide>
              )}

              {error && (
                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: '12px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.2)',
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

              <OtpInput
                inputRef={inputRef}
                value={otp}
                onChange={handleOtpChange}
                placeholder="000000"
                fullWidth
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  autoComplete: 'one-time-code',
                }}
              />

              <StyledFormControlLabel
                control={
                  <Checkbox
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    size="small"
                  />
                }
                label="Trust this device for 30 days"
              />

              <EasyCheckButton
                buttonType="auth"
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                disabled={otp.length !== 6}
                size="large"
                startIcon={<VerifiedUser />}
                sx={{
                  background: themeUtils.gradients.primary,
                  '&:hover': {
                    background: themeUtils.gradients.primaryDark,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                  },
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
                }}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </EasyCheckButton>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the code?{' '}
                  {resendCooldown > 0 ? (
                    <Typography component="span" variant="body2" color="text.disabled">
                      Resend in {resendCooldown}s
                    </Typography>
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleResendOtp}
                      disabled={resendLoading}
                      sx={{
                        color: themeUtils.colors.blue['blue-600'],
                        textDecoration: 'none',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {resendLoading ? 'Sending...' : 'Resend OTP'}
                    </Link>
                  )}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{
                    color: themeUtils.colors.blue['blue-600'],
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <ArrowBack sx={{ fontSize: 16 }} />
                  Back to Login
                </Link>
              </Box>
            </Stack>
          </Box>
        </AnimatedBox>
      </OtpCard>

      <Box sx={{
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center'
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          &copy; 2025 EasyCheck. All rights reserved.
        </Typography>
      </Box>
    </OtpContainer>
  );
};

export default OtpVerificationForm;
